/*
 * Gate de "auth-ready" para las cargas de arranque en frío.
 *
 * Problema (cold-start): al abrir el panel en frío, algunas cargas de mount disparan su
 * request ANTES de que `checkAuth` (AuthContext) haya restaurado el token desde localStorage
 * y propagado el estado de React. El interceptor de `axiosInstance` gatea TODO por la presencia
 * del token, así que esas requests salen SIN `Authorization`/`id_comercio` → el authorizer
 * responde 401/403 → cada servicio hace `.catch(() => toast.error)` → lluvia de toasts
 * "el servicio falló". Recargar funciona porque el token ya está persistido.
 *
 * Este módulo es la primitiva CENTRAL (sin tocar 20 servicios) que permite:
 *   1. Saber si la sesión ya quedó restaurada (`isAuthRestored`).
 *   2. ESPERAR brevemente a que el token aparezca (`waitForAuthReady`) para que el interceptor
 *      de respuesta pueda REINTENTAR de forma transparente una request que se coló sin token.
 *
 * La fuente de verdad del token sigue siendo `localStorage` (lo escribe `AuthContext.login`) y,
 * como fallback PLI-2031, el JWT del repartidor en memoria. Aquí solo lo OBSERVAMOS — no lo
 * mutamos. `signalAuthReady()` permite a `AuthContext` despertar a los waiters de inmediato
 * (sin esperar al poll) cuando login/checkAuth termina de restaurar la sesión.
 */
import { getDeliveryPersonJwt } from './deliveryPersonAxios';

// Lee el token efectivo (localStorage o JWT del repartidor en memoria).
const readToken = (): string | null => {
  try {
    return localStorage.getItem('token') || getDeliveryPersonJwt();
  } catch {
    // En incógnito/sandbox el acceso a Storage puede lanzar; tratamos como "sin token".
    return getDeliveryPersonJwt();
  }
};

// True si ya hay un token disponible (sesión restaurada o login hecho).
export const isAuthRestored = (): boolean => Boolean(readToken());

// True si el error proviene de un fallo TRANSITORIO de cold-start (request que salió sin token y
// cuyos reintentos se agotaron o nunca llegó la sesión). El interceptor de respuesta de
// `axiosInstance` etiqueta esos errores con `isTransientAuthColdStart`. Los `.catch` de las cargas
// de mount lo usan para NO pintar un toast por un fallo de arranque: con el gate+retry esa data
// carga sola al llegar el token; un fallo REAL y persistente (request CON token) nunca se etiqueta
// así, por lo que SÍ informa. Suprimir el toast no oculta data: la reconciliación/refetch la trae.
export const isTransientAuthColdStart = (err: unknown): boolean =>
  Boolean(err && typeof err === 'object' && (err as { isTransientAuthColdStart?: boolean }).isTransientAuthColdStart);

/*
 * ──────────────────────────────────────────────────────────────────────────────
 * Ventana de arranque (startup window) — distingue "cold-start crítico" de "runtime".
 * ──────────────────────────────────────────────────────────────────────────────
 *
 * La causa raíz REAL del toast-storm en prod NO es "request sin token": el panel escribe
 * el token síncrono antes del mount y hay gate `isReady` en el router. Lo que pasa es que
 * la avalancha de ~20-27 fetches concurrentes de mount en frío throttlea al authorizer del
 * panel (TTL=0, sin cache) → 429 → el authorizer cae a su catch → API Gateway responde 500
 * en ráfaga. O sea: los fallos reales de arranque son **429/500 en requests CON token**.
 *
 * Para mitigar sin romper el uso normal necesitamos saber si un fallo ocurre DENTRO de la
 * ventana de arranque (las primeras cargas tras abrir/recargar el panel) o en runtime. Solo
 * en la ventana de arranque aplicamos el último recurso (reload amigable); un 500 puntual en
 * runtime sigue informando normal, sin recargar (eso sería molesto).
 *
 * La ventana se abre al cargar el módulo (= carga de la app) y se cierra al cumplirse el plazo.
 * `STARTUP_WINDOW_MS` cubre el burst inicial de mount-fetches con holgura. No depende del token:
 * el caso problemático es justamente requests CON token que fallan por throttle del authorizer.
 */
const STARTUP_WINDOW_MS = 30_000;

// Marca de tiempo del fin de la ventana de arranque. Se fija al cargar el módulo (carga de la app).
let startupDeadline = Date.now() + STARTUP_WINDOW_MS;

// True si seguimos dentro de la ventana de arranque (primeras cargas críticas tras abrir el panel).
export const isInStartupWindow = (): boolean => Date.now() < startupDeadline;

// Reinicia la ventana de arranque (p.ej. tras un cambio de sucursal que vuelve a montar las
// cargas críticas). Acotado: solo extiende el plazo, nunca crea loops.
export const resetStartupWindow = (): void => {
  startupDeadline = Date.now() + STARTUP_WINDOW_MS;
};

/*
 * ──────────────────────────────────────────────────────────────────────────────
 * Guard de auto-reload (loop-safe) — último recurso cuando el arranque sigue atascado.
 * ──────────────────────────────────────────────────────────────────────────────
 *
 * Si tras los reintentos transparentes las cargas críticas de arranque siguen fallando,
 * recargamos UNA vez la página (el cold-start suele resolverse al segundo intento, cuando el
 * authorizer ya está caliente). El guard vive en `sessionStorage` para que sobreviva al reload
 * pero NO al cierre de pestaña: si ya recargamos una vez y vuelve a fallar, NO recargamos en
 * loop — mostramos un prompt manual amigable. La llave es propia del cold-start y `resetStore()`
 * del login NO la toca (PRJ-001): sessionStorage, no el store de Redux persistido.
 */
const AUTO_RELOAD_KEY = 'coldStartAutoReloaded';

const safeSessionGet = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    // En incógnito/sandbox el acceso a Storage puede lanzar; tratamos como "no marcado".
    return null;
  }
};

const safeSessionSet = (key: string, value: string): boolean => {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    // Sin sessionStorage (modo privado / storage deshabilitado) no podemos garantizar loop-safety
    // del reload. Devolvemos false para que el caller NO recargue (fail-safe): recargar sin poder
    // persistir el flag entraría en loop infinito (el flag no sobreviviría al reload).
    return false;
  }
};

// True si YA disparamos el auto-reload de cold-start en esta pestaña (no volver a recargar).
export const hasColdStartAutoReloaded = (): boolean => safeSessionGet(AUTO_RELOAD_KEY) === '1';

// Marca que ya recargamos por cold-start (persiste el flag a través del reload). Devuelve si la
// persistencia tuvo éxito: si NO (sessionStorage no disponible), el caller NO debe recargar.
export const markColdStartAutoReloaded = (): boolean => safeSessionSet(AUTO_RELOAD_KEY, '1');

// Limpia el guard de auto-reload. Lo llama el arranque exitoso (cuando el panel cargó bien) para
// que un cold-start futuro en esta misma pestaña pueda volver a recuperarse con un reload.
export const clearColdStartAutoReloaded = (): void => {
  try {
    sessionStorage.removeItem(AUTO_RELOAD_KEY);
  } catch {
    // best-effort
  }
};

// Waiters pendientes a los que `signalAuthReady` despierta en cuanto hay token.
const waiters = new Set<() => void>();

// Lo llama `AuthContext` cuando login/checkAuth termina de restaurar la sesión: despierta de
// inmediato a cualquier request que esté esperando el token, sin depender del poll.
export const signalAuthReady = (): void => {
  if (!isAuthRestored()) return;
  waiters.forEach((resolve) => resolve());
  waiters.clear();
};

// Resuelve `true` apenas haya token (vía `signalAuthReady` o por poll de respaldo), o `false`
// si se agota `timeoutMs` sin que aparezca. Pensado para que el interceptor de respuesta espere
// BREVEMENTE al token antes de reintentar una request de cold-start. Si ya hay token, resuelve
// sincrónicamente (sin timers) para no penalizar el camino normal.
export const waitForAuthReady = (timeoutMs = 1500, pollMs = 60): Promise<boolean> => {
  if (isAuthRestored()) return Promise.resolve(true);

  return new Promise<boolean>((resolve) => {
    // Holder mutable para los recursos a limpiar; se llena más abajo. `finish` solo lo referencia
    // (declarado antes), evitando la referencia-adelantada que prohíbe `no-use-before-define`.
    const cleanup: {
      settled: boolean;
      onSignal?: () => void;
      pollId?: ReturnType<typeof setInterval>;
      timeoutId?: ReturnType<typeof setTimeout>;
    } = { settled: false };

    const finish = (value: boolean): void => {
      if (cleanup.settled) return;
      cleanup.settled = true;
      if (cleanup.onSignal) waiters.delete(cleanup.onSignal);
      clearInterval(cleanup.pollId);
      clearTimeout(cleanup.timeoutId);
      resolve(value);
    };

    // Camino rápido: `signalAuthReady` nos despierta apenas login/checkAuth termina.
    cleanup.onSignal = () => finish(true);
    waiters.add(cleanup.onSignal);
    // Respaldo: poll corto por si el token aparece sin pasar por `signalAuthReady`
    // (p.ej. otra pestaña, o un login que no importó este módulo).
    cleanup.pollId = setInterval(() => {
      if (isAuthRestored()) finish(true);
    }, pollMs);
    // Tope: nunca esperar indefinidamente (no convertir un fallo real en un cuelgue).
    cleanup.timeoutId = setTimeout(() => finish(false), timeoutMs);
  });
};
